import { z } from 'zod';

// Vendor portal inventory item categories (INVN system)
export const VendorInventoryCategorySchema = z.enum([
  'TABLE',
  'COURT',
  'ROOM',
  'EQUIPMENT',
  'LANE',
  'OTHER',
]);
export type VendorInventoryCategory = z.infer<typeof VendorInventoryCategorySchema>;

// Vendor portal inventory status (INVN system)
export const VendorInventoryStatusSchema = z.enum([
  'AVAILABLE',
  'BOOKED',
  'MAINTENANCE',
  'INACTIVE',
]);
export type VendorInventoryStatus = z.infer<typeof VendorInventoryStatusSchema>;

// Pricing schema (used in create and update)
export const InventoryPricingSchema = z.object({
  basePrice: z.number()
    .min(0, 'Price cannot be negative')
    .max(100000, 'Price too high'),
  taxRate: z.number()
    .min(0, 'Tax rate cannot be negative')
    .max(100, 'Tax rate cannot exceed 100%')
    .default(18), // Default GST rate
  priceUnit: z.enum(['HOUR', 'SESSION', 'DAY']).default('HOUR'),
});
export type InventoryPricing = z.infer<typeof InventoryPricingSchema>;

// Create inventory schema (INVN-02)
export const CreateVendorInventorySchema = z.object({
  venueId: z.string().uuid('Invalid venue ID'),
  name: z.string().min(1, 'Name is required').max(100, 'Name too long'),
  description: z.string().max(500).optional(),
  category: VendorInventoryCategorySchema,
  quantity: z.number().int().min(1, 'Quantity must be at least 1').max(100),
  pricing: InventoryPricingSchema,
});
export type CreateVendorInventory = z.infer<typeof CreateVendorInventorySchema>;

// Update inventory schema (INVN-03)
export const UpdateVendorInventorySchema = z.object({
  name: z.string().min(1).max(100).optional(),
  description: z.string().max(500).optional(),
  category: VendorInventoryCategorySchema.optional(),
  quantity: z.number().int().min(0).max(100).optional(),
  pricing: InventoryPricingSchema.optional(),
  status: VendorInventoryStatusSchema.optional(),
});
export type UpdateVendorInventory = z.infer<typeof UpdateVendorInventorySchema>;

// Bulk status update schema (INVN-05)
export const BulkStatusUpdateSchema = z.object({
  itemIds: z.array(z.string().uuid()).min(1, 'Select at least one item'),
  status: z.enum(['AVAILABLE', 'INACTIVE']),
});
export type BulkStatusUpdate = z.infer<typeof BulkStatusUpdateSchema>;

// Pricing rule schema (INVN-06)
export const PricingRuleSchema = z.object({
  type: z.enum(['PEAK_HOURS', 'WEEKEND', 'HOLIDAY', 'MEMBER_DISCOUNT']),
  multiplier: z.number().min(0.1).max(5), // 0.1x to 5x price adjustment
  conditions: z.object({
    startTime: z.string().optional(),
    endTime: z.string().optional(),
    daysOfWeek: z.array(z.number().min(0).max(6)).optional(),
  }).optional(),
});
export type PricingRule = z.infer<typeof PricingRuleSchema>;
